import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Phone } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useTwilioCall } from './twilio-device-provider';
import { usePermissions } from '@/hooks/use-permissions';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import type { CompanyPhoneLine } from '@shared/schema';
import { formatPhoneDisplay } from '@shared/utils/phone';

interface ClickToCallButtonProps {
  phoneNumber: string;
  contactName?: string;
  contactId?: string;
  claimId?: string;
  variant?: 'default' | 'ghost' | 'outline' | 'secondary';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showLabel?: boolean;
  customLabel?: string;
  className?: string;
  fallbackComponent?: React.ReactNode;
}

export function ClickToCallButton({
  phoneNumber,
  contactName,
  contactId,
  claimId,
  variant = 'outline',
  size = 'sm',
  showLabel = false,
  customLabel,
  className,
  fallbackComponent,
}: ClickToCallButtonProps) {
  const { toast } = useToast();
  const { makeCall, callState, isReady, error: deviceError } = useTwilioCall();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPhoneLine, setSelectedPhoneLine] = useState<string>('');

  const { data: twilioStatus } = useQuery<{ configured: boolean; message: string }>({
    queryKey: ['/api/calls/config/status'],
    staleTime: 60000,
  });

  const { data: phoneLines = [] } = useQuery<CompanyPhoneLine[]>({
    queryKey: ['/api/calls/phone-lines'],
    enabled: twilioStatus?.configured === true,
  });

  const { isViewer } = usePermissions();

  const isInCall = callState !== 'idle';

  const handleStartCall = (phoneLineId: string) => {
    makeCall({
      to: phoneNumber,
      phoneLineId,
      claimId,
      contactId,
      contactName,
    });
    toast({
      title: 'Calling…',
      description: `Connecting to ${contactName || phoneNumber}`,
    });
    setIsDialogOpen(false);
  };

  const handleCallClick = () => {
    if (!twilioStatus?.configured) {
      toast({
        title: 'Twilio not configured',
        description: 'Please configure Twilio credentials to make calls.',
        variant: 'destructive',
      });
      return;
    }

    if (!isReady) {
      toast({
        title: 'Phone not ready',
        description: deviceError || 'Browser phone is still initializing. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    if (isInCall) {
      toast({
        title: 'Call in progress',
        description: 'Please end the current call before starting a new one.',
        variant: 'destructive',
      });
      return;
    }

    if (phoneLines.length === 0) {
      toast({
        title: 'No phone lines',
        description: 'No phone lines are configured. Please add a phone line in settings.',
        variant: 'destructive',
      });
      return;
    }

    if (phoneLines.length === 1) {
      handleStartCall(phoneLines[0].id);
    } else {
      setIsDialogOpen(true);
    }
  };

  const handleConfirmCall = () => {
    if (!selectedPhoneLine) {
      toast({
        title: 'Select a phone line',
        description: 'Please select which phone line to use for the call.',
        variant: 'destructive',
      });
      return;
    }
    handleStartCall(selectedPhoneLine);
  };

  const isUnavailable = isViewer || !twilioStatus?.configured;

  if (isUnavailable) {
    if (fallbackComponent) {
      return <>{fallbackComponent}</>;
    }
    
    return (
      <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
        <Phone className="h-3 w-3" />
        <span>{customLabel || formatPhoneDisplay(phoneNumber)}</span>
      </span>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleCallClick}
        disabled={isInCall}
        data-testid={`button-call-${contactId || phoneNumber.replace(/\D/g, '')}`}
        className={`gap-1 ${className || ''}`}
      >
        {(showLabel || customLabel) && <span>{customLabel || 'Call'}</span>}
        <Phone className="h-4 w-4" />
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Phone Line</DialogTitle>
            <DialogDescription>
              Choose which company phone line to use for calling {contactName || phoneNumber}.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="phone-line">From Phone Line</Label>
              <Select
                value={selectedPhoneLine}
                onValueChange={setSelectedPhoneLine}
              >
                <SelectTrigger id="phone-line" data-testid="select-phone-line">
                  <SelectValue placeholder="Select a phone line" />
                </SelectTrigger>
                <SelectContent>
                  {phoneLines.filter(l => l.isActive).map((line) => (
                    <SelectItem
                      key={line.id}
                      value={line.id}
                      data-testid={`option-phone-line-${line.id}`}
                    >
                      {line.friendlyName} ({formatPhoneDisplay(line.phoneNumber)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm text-muted-foreground">
              <p><strong>Calling:</strong> {contactName || formatPhoneDisplay(phoneNumber)}</p>
              <p><strong>Number:</strong> {formatPhoneDisplay(phoneNumber)}</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmCall}
              disabled={!selectedPhoneLine}
              data-testid="button-confirm-call"
            >
              <Phone className="mr-2 h-4 w-4" />
              Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
